import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import Quill from 'quill';
import {BlogService} from '../../../../shared/services/blog.service';
import {BlogCategoryService} from '../../../../shared/services/blog-category.service';
import {BlogTagService} from '../../../../shared/services/blog-tag.service';
import {AlertService} from '../../../../shared/services/alert.service';
import {environment} from '../../../../../environments/environment';

@Component({
    selector: 'app-blog-form',
    templateUrl: './blog-form.component.html',
    styleUrls: ['./blog-form.component.scss']
})
export class BlogFormComponent implements OnInit, OnDestroy {
    form: FormGroup;
    isEdit = false;
    blogId: number | string | null = null;
    saving = false;
    loading = false;
    backendUrl = environment.appUrl;

    // Categories / Tags
    categories: any[] = [];
    tags: any[] = [];
    selectedTagIds: number[] = [];

    // Cover image
    imageChangedEvent: any = '';
    croppedBase64: string | null = null;
    existingCoverUrl: string | null = null;
    showCropper = false;

    // Preview
    showPreview = false;

    // Editor
    quillModules: any;

    // Active tab
    activeTab: 'content' | 'seo' = 'content';

    private destroy$ = new Subject<void>();
    private slugManuallyEdited = false;

    constructor(private fb: FormBuilder,
                private route: ActivatedRoute,
                private router: Router,
                private blogService: BlogService,
                private categoryService: BlogCategoryService,
                private tagService: BlogTagService,
                private alertService: AlertService) {
    }

    ngOnInit(): void {
        this.buildQuillModules();

        this.form = this.fb.group({
            title: ['', [Validators.required, Validators.maxLength(255)]],
            slug: [''],
            category_id: [null],
            intro: ['', [Validators.maxLength(1000)]],
            content: [''],
            status: ['DRAFT'],
            is_featured: [false],
            meta_title: [''],
            meta_description: [''],
            meta_keywords: [''],
            published_at: [null]
        });

        this.form.get('slug').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (document.activeElement?.id === 'blog-slug-input') {
                    this.slugManuallyEdited = true;
                }
            });

        this.form.get('title').valueChanges
            .pipe(debounceTime(150), takeUntil(this.destroy$))
            .subscribe(title => {
                if (!this.slugManuallyEdited && title) {
                    this.form.get('slug').setValue(this.slugify(title), {emitEvent: false});
                }
            });

        this.loadTaxonomies();

        this.blogId = this.route.snapshot.paramMap.get('id');
        this.isEdit = !!this.blogId;
        if (this.isEdit) {
            this.loadBlog();
        }
    }

    private buildQuillModules(): void {
        const blogService = this.blogService;
        const alertService = this.alertService;
        const backendUrl = this.backendUrl;

        this.quillModules = {
            toolbar: {
                container: [
                    [{header: [1, 2, 3, false]}],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
                    [{list: 'ordered'}, {list: 'bullet'}, {indent: '-1'}, {indent: '+1'}],
                    [{align: []}, {color: []}, {background: []}],
                    ['link', 'image', 'video'],
                    ['clean']
                ],
                handlers: {
                    image() {
                        const editor: Quill = this.quill;
                        const input = document.createElement('input');
                        input.setAttribute('type', 'file');
                        input.setAttribute('accept', 'image/*');
                        input.click();
                        input.onchange = () => {
                            const file = input.files?.[0];
                            if (!file) {
                                return;
                            }
                            blogService.uploadContentImage(file).subscribe(res => {
                                const url = res?.url?.startsWith('http') ? res.url : (backendUrl + (res?.path || ''));
                                const range = editor.getSelection(true);
                                editor.insertEmbed(range.index, 'image', url, 'user');
                                editor.setSelection(range.index + 1, 0, 'user');
                            }, () => {
                                alertService.showErrors('Image upload failed');
                            });
                        };
                    }
                }
            }
        };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadTaxonomies(): void {
        this.categoryService.list({page: -1}).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.categories = (res && res.data) ? res.data : [];
        });
        this.tagService.list({page: -1}).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.tags = (res && res.data) ? res.data : [];
        });
    }

    loadBlog(): void {
        this.loading = true;
        this.blogService.detail(this.blogId).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.loading = false;
            if (!res) {
                return;
            }
            this.form.patchValue({
                title: res.title,
                slug: res.slug,
                category_id: res.category_id,
                intro: res.intro,
                content: res.content,
                status: res.status,
                is_featured: !!res.is_featured,
                meta_title: res.meta_title,
                meta_description: res.meta_description,
                meta_keywords: res.meta_keywords,
                published_at: res.published_at ? res.published_at.substring(0, 16) : null
            }, {emitEvent: false});
            this.slugManuallyEdited = true;
            this.selectedTagIds = (res.tags || []).map((t: any) => t.id);
            this.existingCoverUrl = res.cover_image
                ? (res.cover_image.startsWith('http') ? res.cover_image : this.backendUrl + res.cover_image)
                : null;
        }, () => {
            this.loading = false;
        });
    }

    onCoverPicked(event: any): void {
        if (event?.target?.files?.length) {
            this.imageChangedEvent = event;
            this.showCropper = true;
        }
    }

    onImageCropped(event: ImageCroppedEvent): void {
        this.croppedBase64 = event.base64;
    }

    cancelCrop(): void {
        this.imageChangedEvent = '';
        this.showCropper = false;
    }

    confirmCrop(): void {
        if (!this.croppedBase64) {
            return;
        }
        this.existingCoverUrl = this.croppedBase64;
        this.showCropper = false;
        this.imageChangedEvent = '';
    }

    removeCover(): void {
        this.existingCoverUrl = null;
        this.croppedBase64 = null;
        this.imageChangedEvent = '';
    }

    toggleTag(tag: any): void {
        const idx = this.selectedTagIds.indexOf(tag.id);
        if (idx >= 0) {
            this.selectedTagIds.splice(idx, 1);
        } else {
            this.selectedTagIds.push(tag.id);
        }
    }

    isTagSelected(tag: any): boolean {
        return this.selectedTagIds.indexOf(tag.id) >= 0;
    }

    addNewTag(name: string): void {
        const trimmed = (name || '').trim();
        if (!trimmed) {
            return;
        }
        this.tagService.create({name: trimmed}).pipe(takeUntil(this.destroy$)).subscribe((tag: any) => {
            if (tag && tag.id) {
                this.tags = [tag, ...this.tags];
                this.selectedTagIds.push(tag.id);
            }
        });
    }

    save(publish = false): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.showErrors('Please fix the highlighted fields');
            return;
        }
        const fd = new FormData();
        const v = this.form.value;
        if (publish) {
            v.status = 'PUBLISHED';
            this.form.get('status').setValue('PUBLISHED');
        }
        Object.keys(v).forEach(key => {
            const val = v[key];
            if (val === null || val === undefined || val === '') {
                return;
            }
            if (key === 'is_featured') {
                fd.append(key, val ? '1' : '0');
                return;
            }
            fd.append(key, val);
        });
        this.selectedTagIds.forEach(id => fd.append('tag_ids[]', String(id)));
        if (this.croppedBase64) {
            fd.append('cover_image_base64', this.croppedBase64);
        } else if (this.isEdit && this.existingCoverUrl === null) {
            fd.append('cover_image', '');
        }

        this.saving = true;
        const obs = this.isEdit
            ? this.blogService.update(this.blogId!, fd)
            : this.blogService.create(fd);
        obs.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.saving = false;
            this.router.navigate(['/blogs']);
        }, () => {
            this.saving = false;
        });
    }

    cancel(): void {
        this.router.navigate(['/blogs']);
    }

    openPreview(): void {
        this.showPreview = true;
    }

    closePreview(): void {
        this.showPreview = false;
    }

    get previewCoverUrl(): string | null {
        return this.croppedBase64 || this.existingCoverUrl;
    }

    private slugify(value: string): string {
        return (value || '')
            .toLowerCase()
            .trim()
            .replace(/['"]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, 80);
    }
}
